// COLUMN UNITS — the horizontal twin of rowUnits.js (TGCLOUD-1908).
//
// The engine lays a chart out on a uniform grid: a person's pixel x is
// `virtualX × NODE_GAP + offset`. Per-node horizontal spacing keeps that layout
// and re-maps it, ROW BY ROW, through the positions the column solve gave the
// row's people (columnSolve.js): `cols.rows[row]` holds the row's breakpoints,
// `us[k]` a person's uniform pixel and `xs[k]` the pixel they are drawn at.
// Between breakpoints the map interpolates; beyond the row's first or last
// person it continues at `slope` (the floor pitch over the engine pitch). Rows
// are independent on purpose: a label in one row never moves a couple in
// another row that no line connects it to (Tim's second QA family).
//
// A pin keeps storing `gx` in GRID units — multiples of NODE_GAP, exactly as
// TGCLOUD-1485 defined it — and gains a precise meaning: a position on the
// engine's uniform grid, read through its own row's map on read and inverted
// through it on write, so a pinned node travels with the people around it.
//
// `cols == null`, or a row with no map, short-circuits every function to the
// legacy expression, byte for byte, which is what keeps a uniform chart (and a
// row the solve had nothing to say about) identical to before.

function interpolate(v, from, to, slope) {
  const m = from.length;
  if (m === 1 || v <= from[0]) return to[0] + (v - from[0]) * slope;
  if (v >= from[m - 1]) return to[m - 1] + (v - from[m - 1]) * slope;
  let lo = 0;
  let hi = m - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (from[mid] <= v) lo = mid;
    else hi = mid;
  }
  const t = (v - from[lo]) / (from[hi] - from[lo]);
  return to[lo] + t * (to[hi] - to[lo]);
}

// The map of one row, or null when there is none to apply.
export function rowMapOf(cols, row) {
  if (!cols || !cols.rows || !Number.isFinite(row)) return null;
  const m = cols.rows[String(Math.round(row))];
  if (
    !m ||
    !Array.isArray(m.us) ||
    !Array.isArray(m.xs) ||
    m.us.length < 1 ||
    m.us.length !== m.xs.length
  ) {
    return null;
  }
  return m;
}

const slopeOf = (cols) => (Number(cols?.slope) > 0 ? Number(cols.slope) : 1);

// Uniform (engine) pixel → drawn pixel, on `row`.
export function xFromUniform(u, cols, row) {
  if (!Number.isFinite(u)) return undefined;
  const m = rowMapOf(cols, row);
  if (!m) return u;
  return interpolate(u, m.us, m.xs, slopeOf(cols));
}

// Drawn pixel → uniform (engine) pixel, on `row`. Exact inverse of
// xFromUniform: a row's breakpoints are strictly increasing on both sides, so
// the map is a bijection.
export function uniformFromX(x, cols, row) {
  if (!Number.isFinite(x)) return undefined;
  const m = rowMapOf(cols, row);
  if (!m) return x;
  return interpolate(x, m.xs, m.us, 1 / slopeOf(cols));
}

// Grid units → drawn pixel. `nodeGap` is the pitch the units were written in.
export function xFromColUnits(gx, cols, nodeGap, row) {
  if (!Number.isFinite(gx)) return undefined;
  const g = Number(nodeGap);
  const m = rowMapOf(cols, row);
  if (!m) return gx * g;
  return xFromUniform(gx * g, cols, row);
}

// Drawn pixel → grid units, the inverse of xFromColUnits.
export function colUnitsFromX(x, cols, nodeGap, row) {
  if (!Number.isFinite(x)) return undefined;
  const g = Number(nodeGap);
  const m = rowMapOf(cols, row);
  if (!m) return x / g;
  return uniformFromX(x, cols, row) / g;
}
